"use client";

import { ConversationEmptyState } from "@workspace/ui/components/ai-elements/conversation";
import {
  Message,
  MessageContent,
  MessageResponse,
} from "@workspace/ui/components/ai-elements/message";
import {
  PromptInput,
  PromptInputActionAddAttachments,
  PromptInputActionMenu,
  PromptInputActionMenuContent,
  PromptInputActionMenuTrigger,
  PromptInputBody,
  PromptInputButton,
  PromptInputFooter,
  type PromptInputMessage,
  PromptInputSubmit,
  PromptInputTextarea,
  PromptInputTools,
} from "@workspace/ui/components/ai-elements/prompt-input";
import { cn } from "@workspace/ui/lib/utils";
import { AnimatePresence, motion } from "framer-motion";
import { Loader2, SparklesIcon } from "lucide-react";
import { useState } from "react";
import selcaIcon from "../../selca-icon-min.png";
import type { MappedRequest } from "../ImagesPage";

type ChatMessage = {
  id: string;
  role: "user" | "assistant";
  content: string;
  image?: string;
  isProcessing?: boolean;
};

type DetailPanelChatProps = {
  request: MappedRequest;
};

export function DetailPanelChat({ request }: DetailPanelChatProps) {
  const [messages, setMessages] = useState<ChatMessage[]>(() =>
    buildInitialMessages(request)
  );
  const [inputText, setInputText] = useState("");
  const [status, setStatus] = useState<"ready" | "streaming">("ready");

  const handleSubmit = (message: PromptInputMessage) => {
    if (!message.text?.trim()) return;

    const userMessage: ChatMessage = {
      id: `user-${Date.now()}`,
      role: "user",
      content: message.text,
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputText("");
    setStatus("streaming");

    setTimeout(() => {
      const assistantMessage: ChatMessage = {
        id: `assistant-${Date.now()}`,
        role: "assistant",
        content:
          "I'll help you with that! This is a simulated response. In a real implementation, this would connect to your AI backend.",
      };
      setMessages((prev) => [...prev, assistantMessage]);
      setStatus("ready");
    }, 1500);
  };

  return (
    <div className="relative flex h-full flex-col overflow-hidden">
      <div className="flex-1 overflow-y-auto px-4 py-4">
        <div className="flex flex-col gap-4">
          {messages.length === 0 ? (
            <ConversationEmptyState
              description="Send a message to start editing this image"
              icon={
                <div className="flex size-16 items-center justify-center rounded-full bg-gradient-to-br from-amber-200 to-orange-300">
                  <img
                    alt="Selca"
                    className="size-10 object-contain"
                    src={selcaIcon}
                  />
                </div>
              }
              title="Start a conversation"
            />
          ) : (
            <AnimatePresence>
              {messages.map((msg) => (
                <ChatBubble key={msg.id} message={msg} />
              ))}
            </AnimatePresence>
          )}
          {status === "streaming" && (
            <motion.div
              animate={{ opacity: 1 }}
              className="flex items-center gap-2 text-muted-foreground"
              initial={{ opacity: 0 }}
            >
              <Loader2 className="size-4 animate-spin" />
              <span className="text-sm">Thinking...</span>
            </motion.div>
          )}
        </div>
      </div>

      <div className="shrink-0 border-t border-zinc-200/30 bg-white/50 backdrop-blur-sm p-3 dark:bg-zinc-900/50 dark:border-zinc-700/30">
        <PromptInput
          className="glass-input-container"
          multiple
          onSubmit={handleSubmit}
        >
          <PromptInputBody>
            <PromptInputTextarea
              className="min-h-[44px] max-h-32 bg-zinc-100/50 dark:bg-zinc-800/50"
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                setInputText(e.target.value)
              }
              placeholder="Describe your edit..."
              value={inputText}
            />
          </PromptInputBody>
          <PromptInputFooter>
            <PromptInputTools>
              <PromptInputActionMenu>
                <PromptInputActionMenuTrigger />
                <PromptInputActionMenuContent>
                  <PromptInputActionAddAttachments />
                </PromptInputActionMenuContent>
              </PromptInputActionMenu>
              <PromptInputButton variant="ghost">
                <SparklesIcon className="size-4" />
              </PromptInputButton>
            </PromptInputTools>
            <PromptInputSubmit
              disabled={!inputText.trim()}
              status={status === "streaming" ? "streaming" : "ready"}
            />
          </PromptInputFooter>
        </PromptInput>
      </div>
    </div>
  );
}

function ChatBubble({ message }: { message: ChatMessage }) {
  const isUser = message.role === "user";

  return (
    <motion.div
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0 }}
      initial={{ opacity: 0, y: 8 }}
      transition={{ duration: 0.2 }}
    >
      <Message from={message.role}>
        <MessageContent
          className={cn(
            isUser
              ? "bg-blue-500 text-white rounded-2xl rounded-br-sm"
              : "bg-zinc-200/80 dark:bg-zinc-700/80 text-foreground rounded-2xl rounded-bl-sm"
          )}
        >
          {message.image && (
            <motion.img
              alt="Attached image"
              className="max-w-[200px] rounded-xl"
              src={message.image}
              whileHover={{ scale: 1.02 }}
            />
          )}
          {message.isProcessing ? (
            <div className="flex items-center gap-2">
              <span>{message.content}</span>
              <Loader2 className="size-4 animate-spin" />
            </div>
          ) : (
            <MessageResponse>{message.content}</MessageResponse>
          )}
        </MessageContent>
      </Message>
    </motion.div>
  );
}

function buildInitialMessages(request: MappedRequest): ChatMessage[] {
  const messages: ChatMessage[] = [];

  if (request.previewUrl) {
    messages.push({
      id: "initial-image",
      role: "user",
      content: "",
      image: request.previewUrl,
    });
  }

  if (request.promptText) {
    messages.push({
      id: "initial-prompt",
      role: "user",
      content: request.promptText,
    });
  }

  if (request.stage === "processing") {
    messages.push({
      id: "processing",
      role: "assistant",
      content: "Processing your request...",
      isProcessing: true,
    });
  } else if (request.stage === "completed") {
    messages.push({
      id: "completed",
      role: "assistant",
      content: "Your image has been processed successfully!",
    });
  } else if (request.stage === "failed") {
    messages.push({
      id: "failed",
      role: "assistant",
      content:
        "Sorry, there was an issue processing your request. Please try again.",
    });
  }

  return messages;
}
